import { Search as SearchIcon, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { EmptyState } from "@/components/empty-state";
import { SearchInput } from "@/components/shared/SearchInput";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCategories, useFlows } from "@/hooks/api/flows";
import { resolveBilingual } from "@/lib/bilingual";
import { useAuthStore } from "@/stores/auth";
import type { Flow, LanguagePreference } from "@/types/api";

const ALL = "all";

export default function FlowListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: flowsData, isLoading, error } = useFlows();
  const { data: categoriesData } = useCategories();
  const languagePreference = useAuthStore((s) => s.user?.language_preference) ?? "BOTH";

  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string>(ALL);

  // Belt-and-suspenders: only ever show published flows here, regardless
  // of what the API returns for the calling role (Admin's list endpoint
  // includes drafts — this page is the "browse & play" surface, so it
  // filters client-side too rather than trusting caller context).
  const publishedFlows = useMemo(
    () => (flowsData?.results ?? []).filter((f) => f.status === "PUBLISHED"),
    [flowsData]
  );

  const filtered = useMemo(() => {
    return publishedFlows.filter((flow) => {
      const matchesCategory = categoryId === ALL || flow.category.id === categoryId;
      const q = query.trim().toLowerCase();
      // Match against both languages regardless of display preference — a
      // bilingual agent may type either.
      const matchesQuery =
        q === "" ||
        flow.title.toLowerCase().includes(q) ||
        flow.title_es.toLowerCase().includes(q) ||
        flow.description.toLowerCase().includes(q) ||
        flow.description_es.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [publishedFlows, categoryId, query]);

  const categories = categoriesData?.results ?? [];
  // Only offer categories that actually have a published flow — an empty
  // filter tab is a dead end.
  const categoriesWithFlows = categories.filter((cat) =>
    publishedFlows.some((f) => f.category.id === cat.id)
  );

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="m-0 text-2xl font-bold text-slate-900">{t("flowList.title")}</h1>
          <p className="mt-1 text-sm text-slate-600">{t("flowList.subtitle")}</p>
        </div>
        <Button
          onClick={() => navigate("/wizard-flows/create")}
          className="shrink-0"
        >
          <Wand2 className="h-4 w-4 mr-2" />
          New Wizard Script
        </Button>
      </div>

      <div className="mt-6 max-w-md">
        <SearchInput
          placeholder={t("flowList.searchPlaceholder")}
          onSearch={setQuery}
          debounceDelay={200}
          size="md"
        />
      </div>

      {categoriesWithFlows.length > 0 && (
        <Tabs value={categoryId} onValueChange={setCategoryId} className="mt-4">
          <TabsList className="h-auto flex-wrap gap-2 border-none">
            <TabsTrigger value={ALL}>{t("flowList.allCategories")}</TabsTrigger>
            {categoriesWithFlows.map((cat) => {
              const label = resolveBilingual(languagePreference, {
                en: cat.name,
                es: cat.name_es,
              });
              return (
                <TabsTrigger key={cat.id} value={cat.id}>
                  {label.primary}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      )}

      <div className="mt-6">
        {isLoading && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        )}

        {error && <p className="text-sm text-danger">{t("flowList.loadError")}</p>}

        {!isLoading && !error && publishedFlows.length === 0 && (
          <EmptyState
            icon={SearchIcon}
            title={t("flowList.noFlowsTitle")}
            description={t("flowList.noFlowsDescription")}
          />
        )}

        {!isLoading && !error && publishedFlows.length > 0 && filtered.length === 0 && (
          <EmptyState
            icon={SearchIcon}
            title={t("flowList.noMatchesTitle")}
            description={t("flowList.noMatchesDescription")}
          />
        )}

        {filtered.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((flow) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                languagePreference={languagePreference}
                onClick={() => navigate(`/flows/${flow.id}/play`)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function FlowCard({
  flow,
  languagePreference,
  onClick,
}: {
  flow: Flow;
  languagePreference: LanguagePreference;
  onClick: () => void;
}) {
  const category = resolveBilingual(languagePreference, {
    en: flow.category.name,
    es: flow.category.name_es,
  });
  const title = resolveBilingual(languagePreference, { en: flow.title, es: flow.title_es });
  const description = resolveBilingual(languagePreference, {
    en: flow.description,
    es: flow.description_es,
  });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="app-card app-card--interactive app-focus-ring"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-primary">
        {category.primary}
        {category.secondary && ` / ${category.secondary}`}
      </div>
      <h2 className="mt-1 text-base font-semibold text-slate-900">{title.primary}</h2>
      {title.secondary && <p className="text-sm text-slate-500">{title.secondary}</p>}
      {description.primary && (
        <p className="mt-1 line-clamp-2 text-sm text-slate-600">{description.primary}</p>
      )}
      {description.secondary && (
        <p className="line-clamp-2 text-sm text-slate-500">{description.secondary}</p>
      )}
    </div>
  );
}
